import { useCallback, useEffect, useMemo } from "react";

import { isLoggedIn } from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";
import { useLocation } from "react-router-dom";

import { ProjectViewLinkInvitationSignIn } from "../components/ProjectViewLinkInvitationSignIn/ProjectViewLinkInvitationSignIn";
import SignIn from "../components/SignIn/SignIn";
import useNavigation from "../hooks/navigation";
import { accountQueryKey } from "../hooks/useAccount";
import type { AccountData } from "../hooks/useAccountTypes";
import { useLogin, useLogout } from "../hooks/useAuth";
import { userNameSchema } from "../hooks/useUpdateName";
import { SearchParam } from "../types";
import { PageSource } from "../types/tracking";
import { searchParamAsOptionalString } from "../urlUtils";
import { isEmailInvalid } from "../utils/validationUtils";

export interface SignInContainerProps {
  isAuthHandoffFailed?: boolean;
}

const SignInContainer: React.FC<SignInContainerProps> = ({ isAuthHandoffFailed }) => {
  const navigation = useNavigation();
  const location = useLocation();

  const appTrackingID = useMemo(() => {
    const queryParams = queryString.parse(location.search);
    return searchParamAsOptionalString(queryParams[SearchParam.TrackingID]);
  }, [location.search]);

  const {
    mutateAsync: mutateLogin,
    isPending: isLoginPending,
    error: loginError,
  } = useLogin({ appTrackingID });

  const queryClient = useQueryClient();

  const redirectCallback = useCallback(async () => {
    try {
      const accountData = await queryClient.ensureQueryData<AccountData>({
        queryKey: accountQueryKey,
      });

      const hasValidName =
        Boolean(accountData.name) && userNameSchema.safeParse(accountData.name).success;
      if (!hasValidName) {
        navigation.goToUpdateNamePage();
        return;
      }
    } catch (err) {
      // Ignore the error here proceed with the redirection
    }
    const isRedirecting = navigation.tryRedirectFromSearchParams(PageSource.Signin);
    if (isRedirecting) {
      return;
    }
    navigation.goToProfile();
  }, [navigation, queryClient]);

  const signIn = async (email: string, password: string, captcha?: string) => {
    try {
      await mutateLogin({ email, password, captcha }, { onSuccess: redirectCallback });
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  const { mutateAsync: mutateLogout } = useLogout({
    redirect: false,
    withRequest: false,
  });
  useEffect(() => {
    if (isLoggedIn()) {
      if (isAuthHandoffFailed) {
        mutateLogout();
      } else {
        redirectCallback();
      }
    }
  }, [redirectCallback, isAuthHandoffFailed, mutateLogout]);

  const emailHint = useMemo(() => {
    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    const emailHint = hashParams.get(SearchParam.EmailHint);
    if (!emailHint || isEmailInvalid(emailHint)) {
      return null;
    }

    return emailHint;
  }, [location]);

  if (emailHint) {
    return (
      <ProjectViewLinkInvitationSignIn
        submit={signIn}
        inProgress={isLoginPending}
        loginError={loginError}
        emailHint={emailHint}
      />
    );
  }

  return (
    <SignIn
      submit={signIn}
      inProgress={isLoginPending}
      loginError={loginError}
      isAuthHandoffFailed={isAuthHandoffFailed}
    />
  );
};

export default SignInContainer;
